import { get } from '../api';
import { renderMarkdown } from './chat';

interface DocEntry { slug: string; title: string; }
interface DocContent { slug: string; title: string; content: string; }

let currentSlug = '';

export function cleanupDocs(): void { currentSlug = ''; }

export async function renderDocs(container: HTMLElement): Promise<void> {
  container.dataset.rendererKind = 'docs';
  injectStyles();

  container.innerHTML = '<div class="docs-wrap" id="docs-wrap"></div>';
  const wrap = container.querySelector('#docs-wrap') as HTMLElement;

  let docs: DocEntry[] = [];
  try {
    docs = await get<DocEntry[]>('/api/docs');
  } catch {
    wrap.innerHTML = '<div class="docs-empty">Failed to load docs.</div>';
    return;
  }

  if (docs.length === 0) {
    wrap.innerHTML = '<div class="docs-empty">No documents yet.</div>';
    return;
  }

  showCards(wrap, docs);
}

function showCards(wrap: HTMLElement, docs: DocEntry[]): void {
  currentSlug = '';
  const cards = docs.map(d => `<button class="docs-card" data-slug="${esc(d.slug)}" type="button">
    <div class="docs-card-icon">
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
    </div>
    <div class="docs-card-title">${esc(d.title)}</div>
    <div class="docs-card-slug">${esc(d.slug)}</div>
  </button>`).join('');

  wrap.innerHTML = `
    <div class="docs-header">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
      <span>Hub Docs</span>
    </div>
    <div class="docs-grid">${cards}</div>
  `;

  wrap.querySelectorAll<HTMLElement>('.docs-card').forEach(card => {
    card.addEventListener('click', () => {
      const slug = card.dataset.slug ?? '';
      if (slug) void showDoc(wrap, docs, slug);
    });
  });
}

async function showDoc(wrap: HTMLElement, docs: DocEntry[], slug: string): Promise<void> {
  currentSlug = slug;
  wrap.innerHTML = '<div class="docs-loading"><div class="docs-spinner"></div>Loading...</div>';

  try {
    const doc = await get<DocContent>(`/api/docs/${encodeURIComponent(slug)}`);
    if (currentSlug !== slug) return;

    const rendered = renderDocMarkdown(doc.content);
    wrap.innerHTML = `
      <div class="docs-header">
        <button class="docs-back" id="docs-back" type="button">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
        </button>
        <span class="docs-header-title">${esc(doc.title)}</span>
      </div>
      <div class="docs-article">
        <div class="docs-article-body">${rendered}</div>
      </div>
    `;

    wrap.querySelector('#docs-back')?.addEventListener('click', () => showCards(wrap, docs));

    wrap.querySelectorAll('.msg-codeblock-copy').forEach(btn => {
      btn.addEventListener('click', () => {
        const raw = (btn.closest('.msg-codeblock-wrap') as HTMLElement | null)?.dataset.codeRaw ?? '';
        void navigator.clipboard.writeText(raw);
        const label = btn.querySelector('.msg-codeblock-copy-label');
        if (label) { label.textContent = 'Copied!'; setTimeout(() => { label.textContent = 'Copy'; }, 1500); }
      });
    });

    const article = wrap.querySelector('.docs-article');
    if (article) article.scrollTop = 0;
  } catch {
    if (currentSlug !== slug) return;
    wrap.innerHTML = '<div class="docs-empty">Failed to load document.</div>';
  }
}

function renderDocMarkdown(text: string): string {
  const tables: string[] = [];
  const lines = text.split('\n');
  const processed: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const curLine = lines[i] ?? '';
    const nextLine = lines[i + 1] ?? '';
    if (i + 1 < lines.length && curLine.includes('|') && /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(nextLine)) {
      const headerCells = parsePipeRow(curLine);
      const alignRow = parsePipeRow(nextLine);
      const aligns = alignRow.map(cell => { const t = cell.trim(); if (t.startsWith(':') && t.endsWith(':')) return 'center'; if (t.endsWith(':')) return 'right'; return 'left'; });
      let table = '<div class="docs-table-wrap"><table class="docs-table"><thead><tr>';
      for (let c = 0; c < headerCells.length; c++) { table += `<th style="text-align:${aligns[c] ?? 'left'}">${esc((headerCells[c] ?? '').trim())}</th>`; }
      table += '</tr></thead><tbody>';
      i += 2;
      while (i < lines.length) {
        const rowLine = lines[i] ?? '';
        if (!rowLine.includes('|') || rowLine.trim() === '') break;
        const cells = parsePipeRow(rowLine);
        table += '<tr>';
        for (let c = 0; c < headerCells.length; c++) { const rendered = esc((cells[c] ?? '').trim()).replace(/`([^`]+)`/g, '<code>$1</code>').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'); table += `<td style="text-align:${aligns[c] ?? 'left'}">${rendered}</td>`; }
        table += '</tr>'; i++;
      }
      table += '</tbody></table></div>';
      tables.push(table); processed.push(`DOCTABLE${tables.length - 1}PLACEHOLDER`);
    } else { processed.push(curLine); i++; }
  }
  let html = renderMarkdown(processed.join('\n'));
  for (let t = 0; t < tables.length; t++) html = html.replace(`DOCTABLE${t}PLACEHOLDER`, tables[t] ?? '');
  return html;
}

function parsePipeRow(line: string): string[] {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|');
}

function esc(s: string): string { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .docs-wrap { display:flex; flex-direction:column; height:100%; overflow-y:auto; min-height:0; }
    .docs-header { display:flex; align-items:center; gap:8px; padding:12px 16px; min-height:48px; border-bottom:1px solid var(--border); flex-shrink:0; font-size:14px; font-weight:600; text-transform:uppercase; letter-spacing:.02em; color:var(--fg-muted); }
    .docs-header svg { opacity:.7; }
    .docs-header-title { font-size:16px; font-weight:700; color:var(--fg); text-transform:none; letter-spacing:0; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .docs-back { background:none; border:none; color:var(--fg-muted); cursor:pointer; padding:4px; min-width:44px; min-height:44px; display:flex; align-items:center; justify-content:center; border-radius:6px; flex-shrink:0; }
    .docs-back:hover { color:var(--fg); background:var(--surface-2-hover); }

    .docs-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:12px; padding:16px; }
    .docs-card { display:flex; flex-direction:column; align-items:center; gap:10px; padding:24px 16px; border:1px solid var(--border); background:var(--bg-secondary); border-radius:10px; cursor:pointer; text-align:center; transition:border-color .15s,background .15s; }
    .docs-card:hover { border-color:var(--accent); background:var(--bg-hover); }
    .docs-card-icon { color:var(--fg-muted); }
    .docs-card:hover .docs-card-icon { color:var(--accent); }
    .docs-card-title { font-size:14px; font-weight:600; color:var(--fg); }
    .docs-card-slug { font-size:11px; color:var(--fg-muted); opacity:.6; font-family:monospace; }

    .docs-article { flex:1; overflow-y:auto; min-height:0; max-width:820px; margin:0 auto; width:100%; padding:24px 24px 64px; }
    .docs-article-body { font-size:14px; line-height:1.65; color:var(--fg-secondary,var(--fg-muted)); }
    .docs-article-body .msg-h1 { font-size:20px; margin:28px 0 12px; color:var(--fg); }
    .docs-article-body .msg-h2 { font-size:16px; margin:24px 0 8px; color:var(--fg); }
    .docs-article-body .msg-h3 { font-size:14px; margin:20px 0 6px; color:var(--fg); font-weight:600; }
    .docs-article-body .msg-hr { border:none; border-top:1px solid var(--border); margin:24px 0; }
    .docs-article-body .msg-blockquote { border-left:3px solid var(--accent); padding:4px 12px; margin:8px 0; color:var(--fg-muted); }
    .docs-article-body strong { color:var(--fg); }
    .docs-article-body .msg-bullet { padding-left:16px; position:relative; margin:2px 0; }
    .docs-article-body .msg-bullet::before { content:'\\2022'; position:absolute; left:4px; color:var(--fg-muted); }
    .docs-article-body .msg-bullet-ordered::before { content:none; }

    .docs-table-wrap { overflow-x:auto; margin:16px 0; }
    .docs-table { width:100%; border-collapse:collapse; font-size:13px; }
    .docs-table th { background:var(--bg-secondary); font-weight:600; text-align:left; padding:8px 12px; border:1px solid var(--border); color:var(--fg); white-space:nowrap; }
    .docs-table td { padding:6px 12px; border:1px solid var(--border); color:var(--fg-muted); }
    .docs-table tr:hover td { background:var(--bg-hover); }
    .docs-table code { background:var(--bg-tertiary); padding:1px 4px; border-radius:3px; font-size:12px; }

    .docs-empty,.docs-loading { display:flex; align-items:center; justify-content:center; height:100%; color:var(--fg-muted); font-size:14px; gap:8px; padding:24px; }
    .docs-spinner { width:16px; height:16px; border:2px solid var(--fg-muted); border-top-color:transparent; border-radius:50%; animation:docs-spin .6s linear infinite; }
    @keyframes docs-spin { to { transform:rotate(360deg); } }

    @media(max-width:768px) {
      .docs-grid { grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; padding:12px; }
      .docs-card { padding:16px 12px; }
      .docs-article { padding:16px 16px 40px; }
      .docs-article-body { font-size:13px; }
      .docs-article-body .msg-h1 { font-size:18px; }
    }
    @media(max-width:420px) {
      .docs-grid { grid-template-columns:1fr 1fr; gap:8px; padding:10px; }
      .docs-article { padding:12px 12px 32px; }
    }
  `;
  document.head.appendChild(style);
}
